import type { Mall } from "../../types/pl";
import { addMonths, diffDays, md, mdw, type Period, type PeriodMode } from "../../lib/plChart";

const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };

const MODES: { key: PeriodMode; label: string }[] = [
  { key: "day", label: "日次" },
  { key: "month", label: "月次" },
  { key: "range", label: "期間指定" },
];

/** minDate の月から今月まで（会計年度が暦年をまたいでも前年の月を選べる）。新しい月が下。 */
function monthOptions(today: string, minDate: string): { value: string; label: string }[] {
  const cur = today.slice(0, 7);
  const opts: { value: string; label: string }[] = [];
  let value = minDate.slice(0, 7);
  for (let i = 0; value <= cur && i < 240; i++) {
    const [y, m] = value.split("-").map(Number);
    const isCurrent = value === cur;
    opts.push({ value, label: `${y}年${m}月${isCurrent ? `（〜${Number(today.slice(8, 10))}日）` : ""}` });
    value = addMonths(value, 1);
  }
  return opts;
}

interface PlPeriodBarProps {
  mode: PeriodMode;
  day: string;
  month: string;
  from: string;
  to: string;
  mall: Mall | "all";
  main: Period;
  compare: Period | null;
  today: string;
  minDate: string;
  showMallFilter: boolean;
  /** 指定すると切替ボタンをその期間モードだけに絞る（財務タブは月次のみ） */
  allowedModes?: PeriodMode[];
  onModeChange: (mode: PeriodMode) => void;
  onDayChange: (day: string) => void;
  onMonthChange: (month: string) => void;
  onFromChange: (from: string) => void;
  onToChange: (to: string) => void;
  onMallChange: (mall: Mall | "all") => void;
}

export function PlPeriodBar({
  mode,
  day,
  month,
  from,
  to,
  mall,
  main,
  compare,
  today,
  minDate,
  showMallFilter,
  allowedModes,
  onModeChange,
  onDayChange,
  onMonthChange,
  onFromChange,
  onToChange,
  onMallChange,
}: PlPeriodBarProps) {
  const lenD = diffDays(main.from, main.to) + 1;
  const periodLabel = mode === "day" ? mdw(main.from) : `${md(main.from)} – ${md(main.to)}（${lenD}日間）`;
  const compareLabel = compare
    ? `比較：${compare.from === compare.to ? md(compare.from) : `${md(compare.from)} – ${md(compare.to)}`}`
    : "比較期間のデータなし";

  const chips: { id: Mall | "all"; label: string; dot: string }[] = [
    { id: "all", label: "全モール", dot: "#1f2523" },
    ...(["shopify", "rakuten", "amazon"] as Mall[]).map((m) => ({ id: m, label: MALL_LABEL[m], dot: MALL_COLOR[m] })),
  ];

  return (
    <div className="pl-period-bar">
      <div className="pl-mode-switch">
        {MODES.filter((m) => !allowedModes || allowedModes.includes(m.key)).map((m) => (
          <button key={m.key} className={`pl-mode-btn ${mode === m.key ? "active" : ""}`} onClick={() => onModeChange(m.key)}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "day" && (
        <input
          type="date"
          className="pl-input"
          value={day}
          min={minDate}
          max={today}
          onChange={(e) => e.target.value && onDayChange(e.target.value)}
        />
      )}
      {mode === "month" && (
        <select className="pl-input" value={month} onChange={(e) => onMonthChange(e.target.value)}>
          {monthOptions(today, minDate).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {mode === "range" && (
        <div className="pl-range-inputs">
          <input type="date" className="pl-input" value={from} min={minDate} max={today} onChange={(e) => e.target.value && onFromChange(e.target.value)} />
          <span style={{ color: "var(--pl-muted)" }}>–</span>
          <input type="date" className="pl-input" value={to} min={minDate} max={today} onChange={(e) => e.target.value && onToChange(e.target.value)} />
        </div>
      )}

      <div className="pl-period-label">
        <span className="pl-period-label-main">{periodLabel}</span>
        {!(allowedModes && !compare) && <span className="pl-period-label-compare">{compareLabel}</span>}
      </div>

      {showMallFilter && (
        <div className="pl-mall-chips">
          {chips.map((c) => (
            <button key={c.id} className={`pl-chip ${mall === c.id ? "active" : ""}`} onClick={() => onMallChange(c.id)}>
              <span className="pl-chip-dot" style={{ background: c.dot }} />
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
